import {
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';

const useAudioPlayer = () => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);

  const [playing, setPlaying] = useState<boolean>();
  const [hasEnded, setEnded] = useState<boolean>(false);

  const togglePlayback = useCallback(() => {
    if (!playing) {
      audioRef.current?.play().catch(() => setPlaying(undefined));
    } else {
      audioRef.current?.pause();
    }
  }, [playing]);

  const handleTimeUpdate = useCallback(() => {
    if (audioRef.current) {
      const { currentTime, duration } = audioRef.current;
      const progress = (currentTime / duration) * 100;
      // Update this directly to prevent unnecessary rerenders from React.
      progressRef.current?.setAttribute('data-progress', `${progress}`);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    // Stop this audio player if any other play events happen.
    document.addEventListener('play', (event) => {
      if (audioRef.current !== event.target) {
        audioRef.current?.pause();
      }
    }, {
      capture: true,
      signal: controller.signal,
    });

    return () => controller.abort();
  });

  return {
    audioRef,
    progressRef,
    playing,
    setPlaying,
    hasEnded,
    setEnded,
    togglePlayback,
    handleTimeUpdate,
  };
};

export default useAudioPlayer;
